import React, { createContext, useContext, useState, useCallback } from 'react';
import { loginWithEmail, validateGoogleToken } from '../api/auth';
import {
  getProfile,
  updateProfile as apiUpdateProfile,
  changePassword as apiChangePassword,
  UpdateProfilePayload,
} from '../api/profile';

const TOKEN_KEY        = 'fininteract_token';
const EMAIL_KEY        = 'fininteract_email';
const NAME_KEY         = 'fininteract_name';
const PHONE_KEY        = 'fininteract_phone';
const ISO_KEY          = 'fininteract_iso';
const ADMIN_TOKEN_KEY  = 'fininteract_admin_token';
const ADMIN_EMAIL_KEY  = 'fininteract_admin_email';
const IMPERSONATE_KEY  = 'fininteract_impersonating';

const parseRoles = (token: string): string[] => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.roles || '').split(',').map((r: string) => r.trim()).filter(Boolean);
  } catch {
    return [];
  }
};

interface AuthUser {
  email:    string;
  token:    string;
  fullName: string;
  phone:    string;
  isoCode:  string;
  roles:    string[];
}

interface AuthContextValue {
  user:             AuthUser | null;
  isAuthenticated:  boolean;
  isAdmin:          boolean;
  impersonating:    string | null;
  login:            (email: string, password: string) => Promise<void>;
  loginWithGoogle:  (idToken: string) => Promise<void>;
  logout:           () => void;
  refreshProfile:   () => Promise<void>;
  updateProfile:    (payload: UpdateProfilePayload) => Promise<void>;
  changePassword:   (current: string, next: string) => Promise<void>;
  startImpersonation: (token: string, email: string) => void;
  exitImpersonation:  () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const email = localStorage.getItem(EMAIL_KEY);
    if (token && email) {
      return {
        token, email,
        fullName: localStorage.getItem(NAME_KEY)  ?? '',
        phone:    localStorage.getItem(PHONE_KEY) ?? '',
        isoCode:  localStorage.getItem(ISO_KEY)   ?? '',
        roles:    parseRoles(token),
      };
    }
    return null;
  });

  const [impersonating, setImpersonating] = useState<string | null>(
    () => localStorage.getItem(IMPERSONATE_KEY)
  );

  const persistUser = useCallback((u: AuthUser) => {
    localStorage.setItem(TOKEN_KEY, u.token);
    localStorage.setItem(EMAIL_KEY, u.email);
    localStorage.setItem(NAME_KEY,  u.fullName);
    localStorage.setItem(PHONE_KEY, u.phone);
    localStorage.setItem(ISO_KEY,   u.isoCode);
    setUser(u);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { token, fullName, isoCode, phone } = await loginWithEmail(email, password);
    persistUser({ email, token, fullName, isoCode, phone, roles: parseRoles(token) });
  }, [persistUser]);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    const { token } = await validateGoogleToken(idToken);
    let email = '';
    try {
      const payloadB64 = idToken.split('.')[1];
      const decoded = JSON.parse(atob(payloadB64));
      email = decoded.email || '';
    } catch {
      email = 'user@google.com';
    }
    persistUser({ email, token, fullName: '', phone: '', isoCode: '', roles: parseRoles(token) });
    try {
      const profile = await getProfile(token);
      persistUser({ email, token, fullName: profile.fullName, phone: profile.phone, isoCode: profile.isoCode, roles: parseRoles(token) });
    } catch { /* best-effort */ }
  }, [persistUser]);

  const refreshProfile = useCallback(async () => {
    if (!user?.token) return;
    const profile = await getProfile(user.token);
    persistUser({ ...user, fullName: profile.fullName, phone: profile.phone, isoCode: profile.isoCode });
  }, [user, persistUser]);

  const updateProfile = useCallback(async (payload: UpdateProfilePayload) => {
    if (!user?.token) throw new Error('Not authenticated');
    await apiUpdateProfile(user.token, payload);
    persistUser({
      ...user,
      fullName: payload.fullName ?? user.fullName,
      phone:    payload.phone    ?? user.phone,
      isoCode:  payload.isoCode  ?? user.isoCode,
    });
  }, [user, persistUser]);

  const changePassword = useCallback(async (current: string, next: string) => {
    if (!user?.token) throw new Error('Not authenticated');
    await apiChangePassword(user.token, current, next);
  }, [user]);

  const logout = useCallback(() => {
    [TOKEN_KEY, EMAIL_KEY, NAME_KEY, PHONE_KEY, ISO_KEY,
     ADMIN_TOKEN_KEY, ADMIN_EMAIL_KEY, IMPERSONATE_KEY].forEach(k => localStorage.removeItem(k));
    setUser(null);
    setImpersonating(null);
    window.location.hash = '/login';
  }, []);

  const startImpersonation = useCallback((token: string, targetEmail: string) => {
    if (!user) return;
    // Stash the real admin credentials
    localStorage.setItem(ADMIN_TOKEN_KEY, user.token);
    localStorage.setItem(ADMIN_EMAIL_KEY, user.email);
    localStorage.setItem(IMPERSONATE_KEY, targetEmail);
    // Swap token
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, targetEmail);
    setImpersonating(targetEmail);
    setUser({ ...user, token, email: targetEmail, roles: parseRoles(token) });
  }, [user]);

  const exitImpersonation = useCallback(() => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const adminEmail = localStorage.getItem(ADMIN_EMAIL_KEY);
    if (!adminToken || !adminEmail) return;
    localStorage.setItem(TOKEN_KEY, adminToken);
    localStorage.setItem(EMAIL_KEY, adminEmail);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EMAIL_KEY);
    localStorage.removeItem(IMPERSONATE_KEY);
    setImpersonating(null);
    setUser(prev => prev ? { ...prev, token: adminToken, email: adminEmail, roles: parseRoles(adminToken) } : null);
  }, []);

  const isAdmin = user?.roles.includes('ADMIN') ?? false;

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: user !== null, isAdmin, impersonating,
      login, loginWithGoogle, logout, refreshProfile, updateProfile, changePassword,
      startImpersonation, exitImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
